import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";

// ============================================
// Styled Components
// ============================================

// 共通のフルページコンテナ（青背景）
const ConceptPage = styled.section`
  min-height: 100dvh;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 64px 24px;
  
  background-color: #009ace;
  
  /* スクロールスナップの設定 */
  scroll-snap-align: start;
`;

// メインヘディング（「動けば、世界は変わる。」と同じスタイル）
const MainHeading = styled(motion.h2)`
  font-family: "A+mfCv-AXISラウンド 50 L StdN", "M PLUS 1p", sans-serif;
  font-size: clamp(1.313rem, 0.849rem + 2.32vw, 2.5rem);
  line-height: 187.5%;
  color: #fff;
  margin-bottom: 48px;
  text-align: center;
  
  @media (max-width: 640px) {
    margin-bottom: 32px;
  }
`;

// 本文コンテナ
const BodyTextContainer = styled.div`
  max-width: 680px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0;
`;

// 段落スタイル
const Paragraph = styled(motion.p)`
  font-family: "A+mfCv-AXISラウンド 50 L StdN", "M PLUS 1p", sans-serif;
  font-size: clamp(0.875rem, 0.8rem + 0.375vw, 1rem);
  color: #fff;
  text-align: justify;
  word-break: normal;
  
  @media (max-width: 640px) {
    text-align: left;
    line-height: 200%;
  }
  
  &:not(:last-child) {
    margin-bottom: 2.5em;
  }
`;

// 原則項目のコンテナ
const PrincipleItem = styled(motion.div)`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 8px;
`;

// 番号カウンター（Counterコンポーネントと同じスタイル、白バージョン）
const NumberCounter = styled.span.attrs({
  className: "heading-spacing-18",
})`
  line-height: 100%;
  letter-spacing: 0;
  user-select: none;
  flex-shrink: 0;

  width: clamp(2.5rem, 2.11rem + 1.95vw, 3.5rem);
  height: clamp(2.5rem, 2.11rem + 1.95vw, 3.5rem);

  border-radius: 50%;
  border: 1px solid #fff;

  display: flex;
  align-items: center;
  justify-content: center;

  background: transparent;
  color: #fff;
`;

// 原則の見出しテキスト（heading-spacing-18スタイル）
const PrincipleHeading = styled.h4.attrs({
  className: "heading-spacing-18",
})`
  color: #fff;
  padding-top: 12px;
  
  ruby {
    ruby-position: over;
  }
  
  rt {
    font-size: 0.4em;
    letter-spacing: 0;
  }
`;

// ============================================
// Animation Variants
// ============================================

const scaleInVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { 
    opacity: 1, 
    scale: 1,
    transition: { duration: 1, ease: "easeOut" }
  }
};

const staggerContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.3
    }
  }
};

const staggerItemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

// ============================================
// Component
// ============================================

const CareersConcept: React.FC = () => {
  return (
    <>
      {/* ページ1: 私たちのプロダクション原則 */}
      <ConceptPage>
        <MainHeading
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.5 }}
          variants={scaleInVariants}
        >
          私たちのプロダクション原則
        </MainHeading>
        
        <BodyTextContainer
          as={motion.div}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.3 }}
          variants={staggerContainerVariants}
        >
          <PrincipleItem variants={staggerItemVariants}>
            <NumberCounter>1</NumberCounter>
            <PrincipleHeading>
              <ruby>概念的完全性<rt>Conceptual Integrity</rt></ruby>
            </PrincipleHeading>
          </PrincipleItem>
          <Paragraph variants={staggerItemVariants}>
            深い創造は、多数決や合議の積み重ねではなく、ひとりの思考が徹底的に掘り下げられることで生まれると考えています。そのため、意思決定者と権限の所在を明確にし、責任を引き受けた個人が、安心して思考と判断に集中できる環境を整えます。
          </Paragraph>
          
          <PrincipleItem variants={staggerItemVariants}>
            <NumberCounter>2</NumberCounter>
            <PrincipleHeading>
              <ruby>多次元調和<rt>Multidimensional Harmony</rt></ruby>
            </PrincipleHeading>
          </PrincipleItem>
          <Paragraph variants={staggerItemVariants}>
            プロダクションとは個ではなく、視点や専門の異なるメンバーが交差し、相互補完することで生まれる豊かさに価値があると考えています。多様なスペシャリストとの共創を重んじ、まだ見ぬ可能性を世界に渡す環境を整えます。
          </Paragraph>
        </BodyTextContainer>
      </ConceptPage>
      
      {/* ページ2 */}
      <ConceptPage>
        <BodyTextContainer
          as={motion.div}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.2 }}
          variants={staggerContainerVariants}
        >
          <Paragraph variants={staggerItemVariants}>
            私たちは、一見矛盾するような、このふたつの両輪を兼ね備えたプロダクションでありたいと考えています。
            概念的完全性が守られる環境により、品質が定まる。そこから明快に翻訳された制作要件やシステムが生まれ、多様なスペシャリストが新たな価値を掛け合わせてくれる。そのことで自らも多くを学び、さらなる循環をもたらす。
          </Paragraph>
          
          <Paragraph variants={staggerItemVariants}>
            私たちは、現場で手を動かしながらも、新しいチャレンジを歓迎します。あらゆるポジションは出発点にしか過ぎず、縛られる必要はないのです。それよりも、仕事に対する最良の解釈に向き合ってみませんか。
          </Paragraph>
                    
          <Paragraph variants={staggerItemVariants}>
            ここでの仕事は、君の価値を次の段階へと押し上げていくはずです。
          </Paragraph>
        </BodyTextContainer>
      </ConceptPage>
    </>
  );
};

export default CareersConcept;
